import { MeetingConfigViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../../models/AppState';
import { convertDurationToString, copyToClipboard } from '../../../../../models/UiUtils';
import { getDurationText } from '../../../../../scheduleMeeting/UiUtils';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { brandPrimary, grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { DeleteConfirmation } from '../../../DeleteConfirmation';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { ClockIcon } from '../../../svgs/icons/ClockIcon';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps {
	meeting: MeetingConfigViewModel;
	onDelete(meeting: MeetingConfigViewModel): void;
	onEdit(meeting: MeetingConfigViewModel): void;
	onClone(meeting: MeetingConfigViewModel): void;
}

const MeetingConfigCardBase: React.FC<IProps> = ({ meeting, onDelete, onEdit, onClone }) => {
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = React.useState(false);

	const onDeleteConfirm = (confirmedDelete: boolean) => {
		if (confirmedDelete) {
			onDelete(meeting);
		}
		setIsDeleteConfirmationOpen(false);
	};

	const onCopyLinkClick = useCallback(() => {
		const done = copyToClipboard(meeting.link);
		if (done) {
			toast.success('Copied meeting link to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting]);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.moreMenu)}>
				<Dropdown>
					<DropdownEllipsisTrigger />
					<DropdownContent>
						<DropdownItem onClick={() => onEdit(meeting)}>Edit</DropdownItem>
						<DropdownItem onClick={() => onClone(meeting)}>Clone</DropdownItem>
						<DropdownItem onClick={() => setIsDeleteConfirmationOpen(true)}>Delete</DropdownItem>
					</DropdownContent>
				</Dropdown>
			</div>

			<h3 className={css(styleSheet.header)}>{meeting.name}</h3>
			<div className={css(styleSheet.clockContainer)}>
				<ClockIcon className={css(styleSheet.clockIcon)} fillColor={grayIconFill} />
				<span className={css(styleSheet.clockText)}>{getDurationText(convertDurationToString(meeting.duration))}</span>
			</div>
			<div className={css(styleSheet.link)}>
				<a
					className={css(baseStyleSheet.brandLink, !meeting.isPublished && styleSheet.linkDisabled)}
					href={meeting.isPublished ? meeting.link : null}
					target='_blank'
					rel='noreferrer'
				>
					{meeting.isPublished ? 'View meeting page' : 'Unpublished'}
				</a>
			</div>
			<button className={css(baseStyleSheet.ctaButtonReverseSmall)} onClick={onCopyLinkClick}>
				<span>
					<ClipboardIcon className={css(styleSheet.clipboardIcon)} fillColor={brandPrimary} />
					Copy Meeting Link
				</span>
			</button>
			{meeting.isBusy && <LoadingSpinner type='small' className={css(baseStyleSheet.absoluteCenter)} />}

			<DeleteConfirmation
				bodyText={`Are you sure you want to delete the "${meeting.name}" meeting?`}
				isOpen={isDeleteConfirmationOpen}
				onFinish={onDeleteConfirm}
			/>
		</div>
	);
};

const MeetingConfigCardAsObserver = observer(MeetingConfigCardBase);
export const MeetingConfigCard = inject(ErrorMessagesViewModelKey)(MeetingConfigCardAsObserver);
